import React, { useState, useEffect } from 'react';
import { BookOpen, RefreshCw, Home } from 'lucide-react';
import ComicBookExport from './ComicBookExport';
import '../styles/MissionComplete.css';
import SYMBOLS from '../utils/symbols';

const MissionComplete = ({ result, panels, comicTitle, onNewMission, onReturnToMenu }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [showComicExport, setShowComicExport] = useState(false);

  useEffect(() => {
    // Staggered animation
    setTimeout(() => setIsVisible(true), 100);
    setTimeout(() => setShowContent(true), 600);
  }, []);

  const getResultConfig = () => {
    switch (result.type) {
      case 'perfect':
        return {
          title: 'PERFECT TIMELINE',
          subtitle: 'Flawless Execution',
          icon: SYMBOLS.STAR,
          colorClass: 'result-perfect',
          description: 'You changed history with surgical precision. The timeline flows exactly as intended.'
        };
      case 'altered':
        return {
          title: 'ALTERED TIMELINE',
          subtitle: 'Mission Accomplished',
          icon: SYMBOLS.DIAMOND,
          colorClass: 'result-altered',
          description: 'History has changed, though not without ripples. The mission is complete.'
        };
      case 'innovative':
        return {
          title: 'INNOVATIVE TIMELINE',
          subtitle: 'Creative Solution',
          icon: SYMBOLS.SPARKLE,
          colorClass: 'result-innovative',
          description: 'You found a path no one expected. Sometimes the best solutions are unorthodox.'
        };
      case 'rules_broken':
        return {
          title: 'RULES BROKEN',
          subtitle: 'Protocol Violation',
          icon: SYMBOLS.WARNING,
          colorClass: 'result-rules',
          description: 'The mission succeeded, but at what cost? Temporal protocols exist for a reason.'
        };
      case 'failed':
        return {
          title: 'HISTORY UNCHANGED',
          subtitle: 'Mission Failed',
          icon: SYMBOLS.CROSS,
          colorClass: 'result-failed',
          description: 'Despite your efforts, events unfolded as they always did. Some moments resist change.'
        };
      case 'corrupted':
        return {
          title: 'TIMELINE CORRUPTED',
          subtitle: 'Critical Failure',
          icon: SYMBOLS.SKULL,
          colorClass: 'result-corrupted',
          description: 'Your actions made things worse. The timeline bears new scars because of this intervention.'
        };
      case 'abandoned':
        return {
          title: 'MISSION ABANDONED',
          subtitle: 'Temporal Retreat',
          icon: SYMBOLS.HOURGLASS,
          colorClass: 'result-abandoned',
          description: 'You retreated from the timeline before making your mark. Some missions are not meant to be.'
        };
      default:
        return {
          title: 'MISSION COMPLETE',
          subtitle: 'Unknown Outcome',
          icon: '?',
          colorClass: 'result-unknown',
          description: 'The timeline has shifted in unexpected ways.'
        };
    }
  };

  const config = getResultConfig();

  // Check if we have enough panels with images for a comic
  const hasComicContent = panels.filter(p => p.role === 'assistant' && p.imageUrl).length > 0;

  return (
    <>
      <div className={`mission-complete-overlay ${isVisible ? 'visible' : ''}`}>
        {/* Dramatic background effects */}
        <div className="complete-background">
          <div className="burst-effect"></div>
          <div className="speed-lines-radial">
            {[...Array(24)].map((_, i) => (
              <div key={i} className="radial-line" style={{ transform: `rotate(${i * 15}deg)` }}></div>
            ))}
          </div>
        </div>

        {/* Main content card */}
        <div className={`complete-card ${config.colorClass} ${showContent ? 'show' : ''}`}>
          {/* Comic-style border */}
          <div className="card-border">
            <div className="border-corner tl"></div>
            <div className="border-corner tr"></div>
            <div className="border-corner bl"></div>
            <div className="border-corner br"></div>
          </div>

          {/* Result icon */}
          <div className="result-icon-container">
            <div className="result-icon">{config.icon}</div>
            <div className="icon-glow"></div>
          </div>

          {/* Title */}
          <div className="result-header">
            <h1 className="result-title">{config.title}</h1>
            <div className="result-subtitle">{config.subtitle}</div>
          </div>

          {/* Divider */}
          <div className="result-divider">
            <span className="divider-line"></span>
            <span className="divider-icon">{SYMBOLS.DIAMOND_BULLET}</span>
            <span className="divider-line"></span>
          </div>

          {/* Description */}
          <div className="result-description">
            <p>{config.description}</p>
          </div>

          {/* Stats */}
          <div className="mission-stats">
            <div className="stat">
              <span className="stat-value">{panels.filter(p => p.playerAction).length}</span>
              <span className="stat-label">Actions Taken</span>
            </div>
            <div className="stat">
              <span className="stat-value">{panels.filter(p => p.imageUrl).length}</span>
              <span className="stat-label">Panels Created</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="complete-actions">
            {/* Comic Book Export Button */}
            {hasComicContent && (
              <button 
                className="action-btn comic-export-btn" 
                onClick={() => setShowComicExport(true)}
              >
                <BookOpen size={18} strokeWidth={2.5} />
                <span>View Comic Book</span>
              </button>
            )}
            
            <button className="action-btn primary" onClick={onNewMission}>
              <RefreshCw size={18} strokeWidth={2.5} />
              <span>New Mission</span>
            </button>
            
            <button className="action-btn secondary" onClick={onReturnToMenu}>
              <Home size={18} strokeWidth={2.5} />
              <span>Main Menu</span>
            </button>
          </div>

          {/* Footer */}
          <div className="card-footer">
            <div className="footer-brand">RIBBON RIDER COMICS</div>
          </div>
        </div>
      </div>

      {/* Comic Book Export Modal */}
      {showComicExport && (
        <ComicBookExport
          panels={panels}
          missionResult={result}
          comicTitle={comicTitle}
          onClose={() => setShowComicExport(false)}
        />
      )}
    </>
  );
};

export default MissionComplete;